import React, { useState, useRef, useEffect, useMemo } from 'react'
import { CheckCircle2, Clock, Play, Check, X, Sparkles, RotateCcw, ChevronsLeftRight } from 'lucide-react'
import PdfViewer from '../components/PdfViewer'
import OrderReview from '../components/OrderReview'
import ConfirmActionModal from '../components/ConfirmActionModal'
import useIsMobile from '../hooks/useIsMobile'
import useAttachmentPreview from '../hooks/useAttachmentPreview'
import { MOCK_QUEUE, formatMinutesAgo, formatClockTime } from '../data/mockOrders'

// Percorso relativo (stessa origine): vedi commento in useAttachmentPreview.js
const API_BASE = ''
const POLL_INTERVAL_MS = 5000

function initials(name) {
  if (!name) return '?'
  return name.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('')
}

// backend-consumer non manda un "nome" del mittente, solo l'email — lo
// deriviamo dalla parte locale (es. "alberto.rossi@x.it" -> "Alberto Rossi").
// Puramente cosmetico per l'avatar/intestazione della card, non un dato reale
// estratto dalla mail.
function deriveSenderName(email) {
  if (!email) return null
  const local = email.split('@')[0]
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(' ')
}

const AVATAR_PALETTE = ['#5B4FCF', '#2E7D6B', '#A8792E', '#B0475A', '#3B6FA8', '#7A5C3E']

// Colore stabile per job_id (non casuale a ogni render) — solo estetico.
function avatarColorFor(jobId) {
  if (!jobId) return AVATAR_PALETTE[0]
  let hash = 0
  for (let i = 0; i < jobId.length; i++) hash = (hash * 31 + jobId.charCodeAt(i)) >>> 0
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length]
}

// Adatta un job reale di backend-consumer (stesso shape usato da Storico) allo
// shape che questa pagina si aspetta. `forwarded_at`/`processed_at` condividono
// lo stesso timestamp: oggi non teniamo traccia separata di quando la mail è
// arrivata rispetto a quando il Queuer ha finito di elaborarla.
function mapJobToQueueItem(job) {
  return {
    job_id: job.job_id,
    // 'completed' = Queuer ha finito, pronto per la revisione umana; qualsiasi
    // altro stato (es. 'processing', arrivato via POST /mail-original prima
    // ancora che parser/LLM/Queuer finiscano) resta "in elaborazione".
    status: job.status === 'completed' ? 'ready' : 'processing',
    sender_name: deriveSenderName(job.sender),
    sender: job.sender,
    subject: job.subject,
    thread: null, // nessun tracking di thread/mail multiple oggi
    forwarded_at: job.timestamp,
    processed_at: job.timestamp,
    avatar_color: avatarColorFor(job.job_id),
    client_name: job.client_name,
    client_piva: job.client_piva,
    client_code: job.client_code,
    client_note: job.client_note,
    order_number: job.order_number,
    destination: job.destination,
    destination_options: job.destination_options,
    order_type: job.order_type,
    body: job.body,
    // Corpo mail puro (OrderReview.jsx, sezione "Testo email originale") e
    // righe con bbox per l'evidenziazione nel PDF (PdfViewer.jsx `lines`
    // prop, via job.attachment_lines?.[filename]) — mancavano entrambi in
    // questa proiezione: Dashboard.jsx tiene il job intero (nessun mapping,
    // vedi setOrders(data.jobs)), qui invece si ricostruiva un oggetto con
    // una whitelist fissa di campi, dimenticando questi due. Bug reale, non
    // cache: confermato da più persone che non vedevano l'evidenziazione
    // SOLO dal flusso Coda (2026-08-11).
    email_body: job.email_body,
    attachment_lines: job.attachment_lines,
    attachment_lines_source: job.attachment_lines_source,
    lines: job.lines || [],
  }
}

// Coda AI: mail inoltrate a ai@jenia.ai in attesa. Una volta estratte
// (status 'ready') si possono revisionare una alla volta — "Inizia
// revisione" apre la prima e, dopo Approva/Rifiuta, passa automaticamente
// alla successiva finché la sessione non finisce. Polling su GET /api/jobs
// (stesso backend-consumer di Storico): include sia i job 'processing'
// (arrivati in anteprima da mail-reader via POST /mail-original, ancora
// dentro parser/LLM/Queuer) sia i 'completed' (pronti per la revisione) —
// una volta approvato/rifiutato un job smette di comparire qui e passa in
// Storico.
export default function Coda() {
  const isMobile = useIsMobile()
  const [queueItems, setQueueItems] = useState([])

  useEffect(() => {
    let cancelled = false

    const fetchQueue = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/jobs`)
        if (!res.ok) throw new Error(`status ${res.status}`)
        const data = await res.json()
        // Mostriamo sia i job ancora in pipeline ('processing', arrivati in
        // anteprima da mail-reader) sia quelli pronti per la revisione
        // ('completed'). Una volta decisi (approved/rejected) spariscono da
        // qui e vanno in Storico.
        const items = (data.jobs || [])
          .filter((j) => j.status === 'processing' || j.status === 'completed')
          .map(mapJobToQueueItem)
        if (!cancelled) setQueueItems(items)
      } catch (err) {
        console.warn('[DEV MOCK] backend-consumer non raggiungibile, uso dati finti:', err.message)
        if (!cancelled) setQueueItems(MOCK_QUEUE)
      }
    }

    fetchQueue()
    const interval = setInterval(fetchQueue, POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [])
  const [manualSelectMode, setManualSelectMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState(() => new Set())

  const [view, setView] = useState('list') // list | review
  // reviewJobId: puntatore all'ordine attualmente mostrato in revisione.
  // manualBatchIds: null = coda VIVA (tutti i pronti, compresi i nuovi
  // arrivi via polling mentre si revisiona); Set = sottoinsieme scelto a
  // mano con "Selezione manuale", fisso per tutta la sessione (non si
  // allarga con nuovi arrivi — e' una selezione intenzionale, non "tutto").
  // PRIMA reviewQueue era uno scatto congelato preso una sola volta
  // all'avvio: un nuovo ordine arrivato mentre si revisionava non ci
  // entrava mai, e la sequenza finiva anche se ce n'erano altri da fare
  // (bug segnalato 2026-08-07). Ora la coda "viva" e' sempre ricalcolata da
  // readyItems (gia' aggiornato dal polling), quindi non puo' esaurirsi
  // finche' c'e' davvero qualcosa da revisionare.
  const [reviewJobId, setReviewJobId] = useState(null)
  const [manualBatchIds, setManualBatchIds] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)
  // Popup di conferma prima di Concludi/Rifiuta — stessa logica di
  // Dashboard.jsx, vedi commento lì.
  const [pendingAction, setPendingAction] = useState(null)

  // Divisore ridimensionabile tra documento e campi estratti (come in
  // Storico) — proporzione di default 40/60 (PDF/righe) su richiesta.
  const reviewBodyRef = useRef(null)
  const [leftWidthPct, setLeftWidthPct] = useState(40)
  const [isDragging, setIsDragging] = useState(false)

  useEffect(() => {
    if (!isDragging) return
    const handleMouseMove = (e) => {
      if (!reviewBodyRef.current) return
      const rect = reviewBodyRef.current.getBoundingClientRect()
      let pct = ((e.clientX - rect.left) / rect.width) * 100
      pct = Math.min(75, Math.max(25, pct))
      setLeftWidthPct(pct)
    }
    const handleMouseUp = () => setIsDragging(false)
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging])

  const readyItems = useMemo(() => queueItems.filter((i) => i.status === 'ready'), [queueItems])
  const processingCount = queueItems.length - readyItems.length

  // N° Ordine Jenia per la revisione da qui — stessa logica di Dashboard.jsx
  // (numerazione progressiva J-1, J-2... in ordine cronologico). TODO: come
  // in Dashboard.jsx, calcolata lato client solo sui job attualmente in coda
  // qui, quindi non garantita coerente con la numerazione vista in Storico
  // finché non esiste un contatore reale lato backend.
  const jeniaOrderNumbers = useMemo(() => {
    const chronological = [...queueItems].sort((a, b) => {
      const tsA = a.processed_at ? new Date(a.processed_at).getTime() : 0
      const tsB = b.processed_at ? new Date(b.processed_at).getTime() : 0
      return tsA - tsB
    })
    const map = new Map()
    chronological.forEach((item, idx) => map.set(item.job_id, `J-${idx + 1}`))
    return map
  }, [queueItems])

  const toggleManualMode = () => {
    setManualSelectMode((v) => !v)
    setSelectedIds(new Set())
  }

  const toggleSelected = (jobId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(jobId)) next.delete(jobId)
      else next.add(jobId)
      return next
    })
  }

  const startCount = manualSelectMode ? selectedIds.size : readyItems.length

  // Il set di ordini che questa sessione di revisione copre — SEMPRE
  // ricalcolato da readyItems (vivo), filtrato sul sottoinsieme fisso solo
  // se e' stata una selezione manuale.
  const sessionItems = useMemo(
    () => (manualBatchIds ? readyItems.filter((i) => manualBatchIds.has(i.job_id)) : readyItems),
    [readyItems, manualBatchIds]
  )

  const startReview = (items, { manual = false } = {}) => {
    if (items.length === 0) return
    setManualBatchIds(manual ? new Set(items.map((i) => i.job_id)) : null)
    setReviewJobId(items[0].job_id)
    setView('review')
  }

  const handleStartReviewClick = () => {
    if (manualSelectMode) {
      startReview(readyItems.filter((i) => selectedIds.has(i.job_id)), { manual: true })
    } else {
      startReview(readyItems)
    }
  }

  // "Inizia da qui": coda viva come il caso generale (non e' una selezione
  // manuale), solo il punto di partenza e' quello scelto — se ne arrivano
  // di nuovi la sessione li assorbe comunque.
  const startFromCard = (item) => {
    setManualBatchIds(null)
    setReviewJobId(item.job_id)
    setView('review')
  }

  const backToList = () => {
    setView('list')
    setReviewJobId(null)
    setManualBatchIds(null)
  }

  const advanceOrFinish = (jobId) => {
    setQueueItems((prev) => prev.filter((i) => i.job_id !== jobId))
    setSelectedIds((prev) => {
      if (!prev.has(jobId)) return prev
      const next = new Set(prev)
      next.delete(jobId)
      return next
    })
    // reviewJobId avanza da solo (vedi effetto sotto) appena sessionItems si
    // ricalcola senza piu' questo job — non serve deciderlo qui.
  }

  const currentJob = sessionItems.find((i) => i.job_id === reviewJobId) || null

  // Tiene viva la sessione: se il job mostrato non e' piu' tra quelli della
  // sessione (appena deciso), passa al prossimo disponibile; se non ce ne
  // sono (nemmeno nuovi arrivi, per la coda non manuale), la chiude. Questo
  // e' il fix del 2026-08-07: prima reviewQueue era uno scatto congelato, un
  // nuovo ordine arrivato durante la revisione non vi entrava mai.
  useEffect(() => {
    if (view !== 'review') return
    if (sessionItems.length === 0) {
      backToList()
      return
    }
    if (!sessionItems.some((i) => i.job_id === reviewJobId)) {
      setReviewJobId(sessionItems[0].job_id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, sessionItems, reviewJobId])

  const {
    pdfDocuments,
    originalFile,
    loading: attachmentLoading,
  } = useAttachmentPreview(currentJob)
  // Contenitore scrollabile condiviso da tutti i PDF impilati in colonna —
  // stessa logica di Dashboard.jsx, vedi commento lì.
  const multiPdfScrollerRef = useRef(null)

  // Collegamento documento↔riga ordine: stessa logica di Dashboard.jsx — vedi
  // commento lì.
  const [hoveredLineText, setHoveredLineText] = useState(null)
  const [scrollRequest, setScrollRequest] = useState({ text: null, nonce: 0 })
  // Testi delle righe ordine confermate — stessa logica di Dashboard.jsx,
  // vedi commento lì.
  const [confirmedLineTexts, setConfirmedLineTexts] = useState([])

  // Banner rosso errori di sistema: stessa logica di Dashboard.jsx — vedi
  // commento lì.
  const [pdfError, setPdfError] = useState(null)
  const [actionError, setActionError] = useState(null)
  const systemErrors = [actionError, pdfError].filter(Boolean)
  // Ultimi override scelti a mano dentro OrderReview — vedi stesso commento in Dashboard.jsx
  const overridesRef = useRef({ client_code: null, destination_code: null, lines: [] })

  useEffect(() => {
    setHoveredLineText(null)
    setScrollRequest({ text: null, nonce: 0 })
    setConfirmedLineTexts([])
    setActionError(null)
    setPendingAction(null)
  }, [currentJob?.job_id])

  // Il codice errore (`err.code`) è quello che arriverà dal backend quando
  // esisterà un campo dedicato (es. `body.error_code`); finché non c'è,
  // ricade sullo status HTTP.
  const submitDecision = async (decision) => {
    // overrides solo su approve: vedi stesso commento in Dashboard.jsx
    const overrides = decision === 'approve' ? overridesRef.current : undefined
    const res = await fetch(`${API_BASE}/api/jobs/${currentJob.job_id}/decision`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ decision, overrides }),
    })
    const body = await res.json().catch(() => ({}))
    if (!res.ok) {
      const problemi = body?.detail?.problemi
      const err = new Error(problemi ? problemi.join('; ') : (body?.error || `status ${res.status}`))
      err.code = body?.error_code || res.status
      throw err
    }
    return body
  }

  const handleApprove = async () => {
    if (!currentJob) return
    setActionLoading(true)
    setActionError(null)
    try {
      await submitDecision('approve')
      advanceOrFinish(currentJob.job_id)
    } catch (err) {
      setActionError({ code: err.code ?? null, message: `Impossibile approvare: ${err.message}` })
    } finally {
      setActionLoading(false)
    }
  }

  const handleReject = async () => {
    if (!currentJob) return
    setActionLoading(true)
    setActionError(null)
    try {
      await submitDecision('reject')
      advanceOrFinish(currentJob.job_id)
    } catch (err) {
      setActionError({ code: err.code ?? null, message: `Impossibile rifiutare: ${err.message}` })
    } finally {
      setActionLoading(false)
    }
  }

  // "Modifica" su un ordine rifiutato — vedi stesso commento in Dashboard.jsx.
  // In Coda.jsx e' un caso limite: appena un ordine viene rifiutato,
  // advanceOrFinish lo rimuove subito da queueItems (il polling qui mostra
  // solo 'processing'/'completed', mai i decisi), quindi normalmente non lo
  // si rivede piu' in questa pagina — questo bottone e' raggiungibile solo
  // da Storico. Per coerenza/robustezza lo gestiamo comunque: dopo la
  // riapertura lato server (torna 'completed'), non abbiamo piu' i suoi
  // dati qui per rimetterlo in sessione al volo, quindi si torna alla
  // lista — il prossimo polling (≤5s) lo far riapparire tra i pronti.
  const handleReopen = async () => {
    if (!currentJob) return
    setActionLoading(true)
    setActionError(null)
    try {
      const res = await fetch(`${API_BASE}/api/jobs/${currentJob.job_id}/reopen`, { method: 'POST' })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(body?.error || `status ${res.status}`)
      backToList()
    } catch (err) {
      setActionError({ code: err.code ?? null, message: `Impossibile riaprire l'ordine: ${err.message}` })
    } finally {
      setActionLoading(false)
    }
  }

  return (
    <div style={s.layout}>
      {view === 'list' && (
        <div style={s.container} className="animate-fade-in">
          <h2 style={s.title}>Ordini in Coda</h2>
          <p style={s.subtitle}>
            Gli ordini inoltrati a <strong>commerciale999@ziggiotto.it</strong> arrivano qui. Clicca <strong>Inizia revisione</strong> per
            scorrere tutti i pronti uno dopo l'altro, oppure parti da un ordine specifico col bottone sulla card.
          </p>

          <div style={s.controlsRow}>
            <span style={{ ...s.statPill, ...s.statPillProcessing }}>
              <Clock size={14} />
              {processingCount} in elaborazione
            </span>
            <span style={{ ...s.statPill, ...s.statPillReady }}>
              <CheckCircle2 size={14} />
              {readyItems.length} pronti per revisione
            </span>
            <button
              style={{ ...s.startReviewBtn, ...(startCount === 0 ? s.startReviewBtnDisabled : {}) }}
              onClick={handleStartReviewClick}
              disabled={startCount === 0}
            >
              <Play size={14} />
              Inizia revisione ({startCount})
            </button>
          </div>

          <label style={s.manualSelectRow}>
            <input type="checkbox" checked={manualSelectMode} onChange={toggleManualMode} style={s.checkbox} />
            Selezione manuale
          </label>

          <div style={s.list}>
            {queueItems.map((item) => (
              <div key={item.job_id} style={s.card}>
                <div style={s.cardMain}>
                  {manualSelectMode && item.status === 'ready' && (
                    <input
                      type="checkbox"
                      checked={selectedIds.has(item.job_id)}
                      onChange={() => toggleSelected(item.job_id)}
                      style={{ ...s.checkbox, flexShrink: 0 }}
                    />
                  )}
                  <span style={{ ...s.avatar, background: item.avatar_color }}>{initials(item.sender_name)}</span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={s.senderRow}>
                      <span style={s.senderName}>{item.sender_name}</span>
                      <span style={s.senderEmail}>&lt;{item.sender}&gt;</span>
                      {item.thread && (
                        <span style={s.threadBadge}>THREAD · {item.thread.count} MSG</span>
                      )}
                    </div>
                    <div style={s.subject}>{item.subject}</div>
                    <div style={s.meta}>
                      Inoltrato {formatMinutesAgo(item.forwarded_at)} · Elaborato alle {formatClockTime(item.processed_at)} · {item.lines?.length ?? 0} righe estratte
                    </div>
                  </div>
                </div>

                <div style={s.cardActions}>
                  <span style={{ ...s.statusPill, ...(item.status === 'ready' ? s.statusReady : s.statusProcessing) }}>
                    {item.status === 'ready' ? <CheckCircle2 size={13} /> : <Clock size={13} />}
                    {item.status === 'ready' ? 'Pronto' : 'In elaborazione'}
                  </span>
                  <button
                    style={{ ...s.startHereBtn, ...(item.status !== 'ready' ? s.startHereBtnDisabled : {}) }}
                    disabled={item.status !== 'ready'}
                    onClick={() => startFromCard(item)}
                  >
                    <Play size={13} />
                    Inizia da qui
                  </button>
                </div>
              </div>
            ))}

            {queueItems.length === 0 && (
              <div style={s.empty}>Nessuna mail in coda al momento.</div>
            )}
          </div>
        </div>
      )}

      {view === 'review' && currentJob && (
        <div style={s.reviewLayout} className="animate-fade-in">
          <div style={s.sequenceBar}>
            <div style={s.sequenceLeft}>
              <Sparkles size={16} style={{ flexShrink: 0 }} />
              <span style={s.sequenceTitle}>
                Sequenza · Ordine {sessionItems.findIndex((i) => i.job_id === reviewJobId) + 1} di {sessionItems.length}
              </span>
              {!isMobile && (
                <span style={s.sequenceSubtitle}>
                  {sessionItems.length > 1
                    ? `Quando approvi (o rifiuti) si passa automaticamente al prossimo (${sessionItems.length - 1} ancora in coda)`
                    : manualBatchIds
                      ? 'Ultimo ordine di questa selezione — quando approvi (o rifiuti) la sequenza sarà completata'
                      : 'Ultimo ordine pronto al momento — se ne arrivano altri la sequenza continua da sola'}
                </span>
              )}
            </div>
            <button style={s.stopSequenceBtn} onClick={backToList}>Pagina principale</button>
          </div>

          <div
            style={{
              ...s.reviewBody,
              flexDirection: isMobile ? 'column' : 'row',
              cursor: isDragging ? 'col-resize' : 'default',
              userSelect: isDragging ? 'none' : 'auto',
            }}
            ref={reviewBodyRef}
          >
            <div style={isMobile ? { ...s.pdfPanel, flex: '0 0 42vh' } : { ...s.pdfPanel, flex: `0 0 ${leftWidthPct}%` }}>
              {currentJob.email_body && currentJob.email_body.trim() && (
                <details style={s.emailBodyBox}>
                  <summary style={s.emailBodySummary}>Testo email originale</summary>
                  <div style={s.emailBodyText}>{currentJob.email_body}</div>
                </details>
              )}
              {pdfDocuments.length > 0 ? (
                <div style={s.multiPdfScroller} ref={multiPdfScrollerRef}>
                  {pdfDocuments.map((doc) => (
                    <div key={doc.filename} style={s.multiPdfBlock}>
                      <div style={s.multiPdfLabel}>{doc.filename}</div>
                      <PdfViewer
                        pdfBase64={doc.base64}
                        highlightText={hoveredLineText}
                        scrollRequest={scrollRequest}
                        onError={setPdfError}
                        lines={currentJob.attachment_lines?.[doc.filename]}
                        linesSource={currentJob.attachment_lines_source?.[doc.filename]}
                        confirmedTexts={confirmedLineTexts}
                        scrollContainerRef={multiPdfScrollerRef}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ flex: 1, minHeight: 0 }}>
                  <PdfViewer
                    pdfBase64={null}
                    textContent={!attachmentLoading ? currentJob.body : null}
                    originalFile={originalFile}
                    onError={setPdfError}
                  />
                </div>
              )}
            </div>

            {!isMobile && (
              <div style={s.resizeHandle} onMouseDown={() => setIsDragging(true)}>
                <div style={{ ...s.resizeBar, background: isDragging ? 'var(--jenia-violet)' : 'var(--hairline-strong)' }} />
                {/* Indizio visivo che il divisore si trascina — piccolissimo
                    e quasi invisibile su richiesta, vedi Dashboard.jsx. */}
                <ChevronsLeftRight size={15} style={s.resizeHint} />
              </div>
            )}

            <div style={{ ...s.rightPanel, flex: 1, maxWidth: 'none' }}>
              <OrderReview
                job={currentJob}
                jeniaOrderNumber={jeniaOrderNumbers.get(currentJob?.job_id)}
                onLineHover={(line) => setHoveredLineText(line?.raw_text || line?.description || null)}
                onLineActivate={(line) => setScrollRequest((prev) => ({ text: line?.raw_text || line?.description || null, nonce: prev.nonce + 1 }))}
                onConfirmedLinesChange={setConfirmedLineTexts}
                systemErrors={systemErrors}
                onOverridesChange={(next) => { overridesRef.current = next }}
              />

              {currentJob.status === 'approved' ? (
                <div style={s.actionButtons}>
                  <span style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
                    Ordine approvato — non modificabile.
                  </span>
                </div>
              ) : currentJob.status === 'rejected' ? (
                <div style={s.actionButtons}>
                  <button className="btn btn-primary" onClick={handleReopen} disabled={actionLoading} style={{ flex: 1 }}>
                    <RotateCcw size={16} />
                    {actionLoading ? 'Riapertura...' : 'Modifica'}
                  </button>
                </div>
              ) : (
                <div style={s.actionButtons}>
                  <button className="btn btn-primary" onClick={() => setPendingAction('approve')} disabled={actionLoading} style={{ flex: 1 }}>
                    <Check size={16} />
                    {actionLoading ? 'Concludendo...' : 'Concludi ordine'}
                  </button>
                  <button className="btn btn-danger" onClick={() => setPendingAction('reject')} disabled={actionLoading} style={{ flex: 1 }}>
                    <X size={16} />
                    {actionLoading ? 'Rifiuto...' : 'Rifiuta'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmActionModal
        open={pendingAction === 'approve'}
        title="Concludere l'ordine?"
        message="Una volta concluso, l'ordine non sarà più modificabile: la scrittura a database è definitiva."
        confirmLabel="Concludi ordine"
        onConfirm={() => { setPendingAction(null); handleApprove() }}
        onCancel={() => setPendingAction(null)}
      />
      <ConfirmActionModal
        open={pendingAction === 'reject'}
        title="Rifiutare l'ordine?"
        message="Potrai modificarlo in seguito: un ordine rifiutato resta nella revisione e può essere ripreso in qualsiasi momento."
        confirmLabel="Rifiuta"
        tone="danger"
        onConfirm={() => { setPendingAction(null); handleReject() }}
        onCancel={() => setPendingAction(null)}
      />
    </div>
  )
}

const s = {
  layout: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
  },
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: 24,
    overflow: 'auto',
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: 'var(--text)',
    margin: 0,
  },
  subtitle: {
    fontSize: 13.5,
    color: 'var(--text-2)',
    lineHeight: 1.6,
    margin: '8px 0 18px 0',
    maxWidth: 760,
  },
  controlsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 14,
    flexWrap: 'wrap',
  },
  statPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '7px 14px',
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 600,
    border: '1px solid transparent',
  },
  statPillProcessing: {
    color: 'var(--warn)',
    background: 'var(--warn-soft-bg)',
    borderColor: 'var(--warn-border)',
  },
  statPillReady: {
    color: 'var(--ok)',
    background: 'var(--ok-soft-bg)',
    borderColor: 'var(--ok-border)',
  },
  startReviewBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    padding: '9px 16px',
    height: 36,
    marginLeft: 'auto',
    border: 'none',
    borderRadius: 10,
    color: '#FFFFFF',
    fontWeight: 600,
    fontSize: 13.5,
    fontFamily: 'inherit',
    background: 'linear-gradient(135deg, #38bdf8 0%, #3b82f6 50%, #1e40af 100%)',
    boxShadow: '0 4px 14px rgba(59,130,246,0.20), 0 2px 6px rgba(30,64,175,0.10)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  startReviewBtnDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
  manualSelectRow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    width: 'fit-content',
    padding: '10px 14px',
    marginBottom: 18,
    background: '#FFFFFF',
    border: '1px solid var(--hairline)',
    borderRadius: 10,
    fontSize: 13,
    color: 'var(--text-2)',
    cursor: 'pointer',
  },
  checkbox: {
    width: 15,
    height: 15,
    accentColor: 'var(--jenia-violet)',
    cursor: 'pointer',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  card: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 16,
    background: '#FFFFFF',
    border: '1px solid var(--hairline)',
    borderRadius: 14,
    padding: '16px 18px',
  },
  cardMain: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
    flex: 1,
  },
  avatar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: '50%',
    fontSize: 13,
    fontWeight: 700,
    color: '#FFFFFF',
    flexShrink: 0,
  },
  senderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  senderName: {
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--text)',
  },
  senderEmail: {
    fontSize: 12.5,
    color: 'var(--text-3)',
  },
  threadBadge: {
    fontSize: 10.5,
    fontWeight: 700,
    color: 'var(--jenia-violet)',
    background: 'var(--grad-jenia-soft)',
    padding: '2px 8px',
    borderRadius: 999,
    letterSpacing: '0.02em',
  },
  subject: {
    fontSize: 14.5,
    fontWeight: 600,
    color: 'var(--text)',
    marginTop: 3,
  },
  meta: {
    fontSize: 12.5,
    color: 'var(--text-3)',
    marginTop: 4,
  },
  cardActions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 8,
    flexShrink: 0,
  },
  statusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '4px 10px',
    borderRadius: 999,
    fontSize: 11.5,
    fontWeight: 600,
    border: '1px solid transparent',
  },
  statusReady: {
    color: 'var(--ok)',
    background: 'var(--ok-soft-bg)',
    borderColor: 'var(--ok-border)',
  },
  statusProcessing: {
    color: 'var(--warn)',
    background: 'var(--warn-soft-bg)',
    borderColor: 'var(--warn-border)',
  },
  startHereBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '7px 14px',
    border: 'none',
    borderRadius: 999,
    color: '#FFFFFF',
    fontWeight: 600,
    fontSize: 12.5,
    fontFamily: 'inherit',
    background: 'linear-gradient(135deg, #38bdf8 0%, #3b82f6 50%, #1e40af 100%)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  startHereBtnDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  empty: {
    padding: '40px 16px',
    textAlign: 'center',
    color: 'var(--text-3)',
    fontSize: 13,
  },
  reviewLayout: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  reviewTopBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 20px',
    borderBottom: '1px solid var(--hairline)',
    background: '#FFFFFF',
  },
  sequenceBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 10,
    padding: '14px 24px',
    background: 'linear-gradient(135deg, #38bdf8 0%, #3b82f6 50%, #1e40af 100%)',
    color: '#FFFFFF',
  },
  sequenceLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    minWidth: 0,
    flex: 1,
  },
  sequenceTitle: {
    fontSize: 14,
    fontWeight: 700,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  sequenceSubtitle: {
    fontSize: 13,
    fontWeight: 400,
    opacity: 0.9,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  stopSequenceBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '8px 16px',
    background: 'rgba(255,255,255,0.16)',
    border: '1px solid rgba(255,255,255,0.4)',
    borderRadius: 999,
    color: '#FFFFFF',
    fontWeight: 600,
    fontSize: 13,
    fontFamily: 'inherit',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  reviewBody: {
    flex: 1,
    display: 'flex',
    gap: 12,
    padding: 12,
    overflow: 'hidden',
  },
  pdfPanel: {
    flex: 1,
    minWidth: 0,
    background: '#FFFFFF',
    border: '1px solid var(--hairline)',
    borderRadius: 12,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  emailBodyBox: {
    flex: '0 0 auto',
    borderBottom: '1px solid var(--hairline)',
    background: 'var(--bg-subtle)',
  },
  emailBodySummary: {
    cursor: 'pointer',
    padding: '8px 12px',
    fontSize: 12.5,
    fontWeight: 500,
    color: 'var(--text-2)',
  },
  emailBodyText: {
    padding: '0 12px 12px',
    fontSize: 12.5,
    color: 'var(--text-2)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: 160,
    overflowY: 'auto',
  },
  // Colonna scrollabile con tutti gli allegati PDF impilati — stessa logica
  // di Dashboard.jsx, vedi commento lì.
  multiPdfScroller: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    position: 'relative',
    background: '#FFFFFF',
  },
  multiPdfBlock: {
    borderBottom: '8px solid var(--bg-subtle)',
  },
  multiPdfLabel: {
    position: 'sticky',
    top: 0,
    zIndex: 5,
    padding: '8px 14px',
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--text-2)',
    background: 'var(--bg-subtle)',
    borderBottom: '1px solid var(--hairline)',
  },
  resizeHandle: {
    position: 'relative',
    flex: '0 0 14px',
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'center',
    cursor: 'col-resize',
  },
  // Vedi Dashboard.jsx: icona centrata, colore quasi fuso con lo sfondo.
  resizeHint: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    // Vedi Dashboard.jsx: un po' più grande e più leggibile di prima.
    color: 'var(--text-3)',
    pointerEvents: 'none',
  },
  resizeBar: {
    width: 2,
    borderRadius: 999,
    transition: 'background 120ms ease',
  },
  rightPanel: {
    flex: '1 1 480px',
    maxWidth: 560,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  actionButtons: {
    display: 'flex',
    gap: 12,
    marginTop: 24,
  },
}
